import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useNavigate } from "react-router-dom";
import useSchoolQueries, {
  normalizeSchoolDetails,
} from "../store/schools/schoolsQueries";
import StudentTable from "../shared/components/Table/StudentTable";
import SchoolDetailsPopup from "../shared/components/SchoolDetailsPopup/SchoolDetailsPopup";
import Pagination from "../shared/components/Pagination/Pagination";
import SearchInputWithDropdown from "../shared/components/SearchInputWithDropdown/SearchInputWithDropdown";
import loader from "../assets/gifs/loader.gif";
import "./Schools.css";
import VisibilityIcon from "@mui/icons-material/Visibility";
import warning_triangle_icon from "../assets/images/warning-triangle.svg";

const SEARCH_MESSAGES = {
  emptySearch: "Schools will appear once a search term is entered",
  noResults: "No matching search results found. Try a different search term.",
  remainingChars: (count) =>
    `Type ${count} more ${
      count === 1 ? "character" : "characters"
    } to start searching`,
  phoneEmpty: "Enter phone number to search",
  phoneInvalid: "Please enter a valid phone number",
  phoneRemainingDigits: (count) =>
    `Enter ${count} more ${count === 1 ? "digit" : "digits"} to search`,
  invitationCodeEmpty: "Enter invitation code to search",
  invitationCodeInvalid:
    "Invitation code must match format: XXXX-XXXXXX-XXXXXX",
};

const SEARCH_OPTIONS = [
  {
    label: "School Name",
    value: "school_name",
    placeholder: "Search by school name",
  },
  {
    label: "School Phone",
    value: "school_phone",
    placeholder: "Search by school phone number",
  },
  {
    label: "School ID",
    value: "school_id",
    placeholder: "Search by school ID",
  },
  {
    label: "UDISE Code",
    value: "udise_code",
    placeholder: "Search by UDISE Code",
  },
  {
    label: "Invitation Code",
    value: "invitation_code",
    placeholder: "Search by invitation code",
  },
];

const Schools = () => {
  const {
    schools,
    totalCount,
    currentPage,
    itemsPerPage,
    searchBy,
    isPhoneSearch,
    isValidPhone,
    isInvitationCodeSearch,
    isValidInvitationCode,
    isLoading,
    error,
    handlePageChange,
    handleLimitChange,
    handleSearchByChange,
    handleSearchQueryChange,
  } = useSchoolQueries(1, 100);

  const [selectedSchool, setSelectedSchool] = useState(null);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const searchInputLength = searchInput.trim().length;

  // Extract digits only from input for phone validation display
  const phoneDigitsFromInput = searchInput.trim().replace(/\D/g, "");

  // Phone: exactly 10 digits. Invitation code: exact pattern. Others: at least 3 characters
  const shouldSearch = isPhoneSearch
    ? isValidPhone
    : isInvitationCodeSearch
      ? isValidInvitationCode
      : searchInputLength >= 3;
  const isResultLimitReached = shouldSearch && schools?.length === 20;
  const contentVariants = {
    initial: {
      opacity: 0,
      y: 24,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.55,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
    exit: {
      opacity: 0,
      y: 24,
      transition: {
        duration: 0.55,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  useEffect(() => {
    setSearchInput("");
  }, [searchBy]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      handleSearchQueryChange(searchInput);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchInput, handleSearchQueryChange]);

  const handleSearch = (value) => {
    let filteredValue = value;

    if (searchBy === "school_phone") {
      // Phone search: only allow digits
      filteredValue = value.replace(/\D/g, "");
    } else if (searchBy === "school_name") {
      // School name search: only allow alphanumeric characters and spaces
      // Remove special characters but keep letters, numbers, and spaces
      filteredValue = value.replace(/[^a-zA-Z0-9\s]/g, "");
    } else if (searchBy === "invitation_code") {
      // Only alphanumeric and hyphen (no special chars within blocks)
      filteredValue = value.replace(/[^A-Za-z0-9-]/g, "");
    }

    setSearchInput(filteredValue);
  };

  const handleRowClick = (school, action) => {
    if (action === "viewStudents") {
      handleViewStudents(school);
      return;
    }

    if (action === "viewDetails") {
      setSelectedSchool(normalizeSchoolDetails(school));
      setIsPopupVisible(true);
    }
  };

  const navigate = useNavigate();

  const handleViewStudents = (school) => {
    navigate(`/students?schoolId=${school.school_id}`);
  };

  const columns = [
    { key: "school_id", label: "SCHOOL ID" },
    { key: "school_name", label: "SCHOOL NAME" },
    { key: "udise_code", label: "UDISE CODE" },
    { key: "address", label: "ADDRESS" },
    { key: "onboard", label: "ONBOARDED" },
    {
      key: "viewDetails",
      label: "Details",
      render: (school) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(school, "viewDetails");
          }}
          className="view-details-button"
        >
          View{" "}
          <VisibilityIcon style={{ fontSize: "12px", marginLeft: "4px" }} />
        </button>
      ),
    },
    { key: "viewStudents", label: "" },
  ];

  return (
    <div className="schools-mainCont">
      <div className="headerCont">
        <div className="titlecont">
          <h3>Schools</h3>
        </div>
      </div>
      <div className="filters-wrapper">
        <SearchInputWithDropdown
          options={SEARCH_OPTIONS}
          selectedOption={searchBy}
          onOptionChange={handleSearchByChange}
          searchValue={searchInput}
          onSearchChange={handleSearch}
        />
        {shouldSearch && totalCount > 20 ? (
          <Pagination
            totalItems={totalCount}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            itemsPerPageOptions={[50, 100, 500, 1000]}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleLimitChange}
          />
        ) : null}
      </div>
      <div className="tableCont">
        <AnimatePresence mode="wait">
          {error ? (
            <motion.div
              key="error"
              variants={contentVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <div className="loaderCont" style={{ color: "#f00" }}>
                <div style={{ fontWeight: 500, marginBottom: "8px" }}>
                  Error loading schools
                </div>
                <div style={{ fontSize: "16px" }}>
                  {error.message || "Failed to fetch schools"}
                </div>
              </div>
            </motion.div>
          ) : !shouldSearch ? (
            <motion.div
              key="empty-search"
              variants={contentVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <div className="loaderCont">
                {isPhoneSearch
                  ? searchInputLength === 0
                    ? SEARCH_MESSAGES.phoneEmpty
                    : phoneDigitsFromInput.length === 0
                      ? SEARCH_MESSAGES.phoneInvalid
                      : SEARCH_MESSAGES.phoneRemainingDigits(
                          Math.max(0, 9 - phoneDigitsFromInput.length),
                        )
                  : isInvitationCodeSearch
                    ? searchInputLength === 0
                      ? SEARCH_MESSAGES.invitationCodeEmpty
                      : SEARCH_MESSAGES.invitationCodeInvalid
                    : searchInputLength === 0
                      ? SEARCH_MESSAGES.emptySearch
                      : SEARCH_MESSAGES.remainingChars(3 - searchInputLength)}
              </div>
            </motion.div>
          ) : isLoading ? (
            <motion.div
              key="loading"
              variants={contentVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <div className="loaderCont">
                <img
                  src={loader}
                  alt="Loading spinner"
                  style={{ width: "200px", height: "200px", margin: "auto" }}
                />
              </div>
            </motion.div>
          ) : totalCount === 0 ? (
            <motion.div
              key="no-results"
              variants={contentVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <div className="loaderCont">{SEARCH_MESSAGES.noResults}</div>
            </motion.div>
          ) : (
            <motion.div
              key="data-table"
              variants={contentVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              {isResultLimitReached && (
                <div className="search-limit-message">
                  <img
                    src={warning_triangle_icon}
                    alt="Warning"
                    className="search-limit-message-icon"
                  />
                  <span>
                    Your search returned{" "}
                    <span className="search-limit-highlight">
                      more than 20 results.
                    </span>{" "}
                    Refine your query for precise results.
                  </span>
                </div>
              )}
              <StudentTable
                data={schools}
                columns={columns}
                showRowSelector={false}
                onRowClick={handleRowClick}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <SchoolDetailsPopup
        school={selectedSchool}
        isVisible={isPopupVisible}
        onClose={() => {
          setIsPopupVisible(false);
          setSelectedSchool(null);
        }}
      />
    </div>
  );
};

export default Schools;
